// 予約完了画面 (/reservations/{予約トークン}/complete) から店舗詳細へ戻るための退避先
// (JPTP_CANSEI-1522)。Stripe の success_url には caseToken / shopId が含まれないため、
// 決済へ出る直前に sessionStorage へ退避し、復帰後に読み出す。
// sessionStorage は同一タブの Stripe 往復で保持されるため復帰後も読める。
// 退避が無い (メール流入・別タブ) 場合は null を返し、呼び出し側はメール導線の案内へ倒す。
const STORAGE_KEY = "viajero.guest.lastShopLocation";

export type ShopLocation = {
  caseToken: string;
  shopId: string;
};

/**
 * sessionStorage から読んだ生文字列を ShopLocation として検証する純関数。
 * 未保存 (null)・壊れた JSON・型不一致・空文字はすべて null を返す。
 * Web Storage を触らないため bun test で単体テストできる (concierge の authBounceGuard と同方式)。
 */
export function parseShopLocation(raw: string | null): ShopLocation | null {
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof parsed !== "object" || parsed === null) return null;
  const { caseToken, shopId } = parsed as Partial<ShopLocation>;
  if (typeof caseToken !== "string" || caseToken === "") return null;
  if (typeof shopId !== "string" || shopId === "") return null;
  return { caseToken, shopId };
}

/**
 * 戻り先を退避する。Stripe へリダイレクトする直前に呼ぶ。
 * プライベートモード等で setItem が throw しても決済を止めないよう握り潰す
 * (退避の失敗は「戻るボタンが出ない」に留める)。
 *
 * 空文字は遷移先を組み立てられず loadLastShopLocation でも弾かれるため、保存側でも捨てる
 * (「保存したのに戻るボタンが出ない」という無言の不整合を作らない)。
 */
export function saveLastShopLocation(location: ShopLocation): void {
  if (!location.caseToken || !location.shopId) return;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(location));
  } catch {
    // 退避できなくても決済フローは継続する。
  }
}

/** 退避した戻り先を読み出す。未保存・不正な値は null。 */
export function loadLastShopLocation(): ShopLocation | null {
  try {
    return parseShopLocation(window.sessionStorage.getItem(STORAGE_KEY));
  } catch {
    return null;
  }
}
